import React, { useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { withTranslation } from "react-i18next";
import { Checkbox, FormControlLabel, Grid } from "@mui/material";

import {
  applicationIdsForChangeMeter,
  applicationTypes,
  otherMeterId,
} from "common/constant";
import SelectField from "Components/Common/SelectField";
import { getOptionLabel, getOptionValue } from "common/enums";

import { Text } from "./styles";
import { useLazyGetAvailableMetersQuery } from "apis/UtilityConsumerMeter";

const applicationIdsForNewMeter = [
  applicationTypes["Permanent Connection"],
  applicationTypes["Temporary Connection"],
];

const otherOption = {
  label: "Other",
  value: otherMeterId,
};

const ConsumerDetails = ({
  formik,
  applicationTypeId,
  applicationId,
  isUtilityConsumerModal,
  t,
}) => {
  const [
    getAvailableMeters,
    { data: { data: availableMeters } = {}, isLoading },
  ] = useLazyGetAvailableMetersQuery();

  const meters = useMemo(
    () =>
      availableMeters?.map(({ srNo, id }) => ({ label: srNo, value: id })) ??
      [],
    [availableMeters]
  );

  const {
    values: {
      consumerInfo: {
        id: consumerId,
        name,
        address,
        serviceNo,
        typeOfConnection,
        contractLoad,
        typeOfTariff,
      },
      extraValues: { isMeterChanged, meterId },
    },
    setFieldValue,
  } = formik;

  const isListOfMetersVisible = useMemo(
    () =>
      ((applicationIdsForNewMeter.includes(applicationTypeId) ||
        isMeterChanged) &&
        applicationTypeId !== applicationTypes["Removal of Connection"]) ||
      isUtilityConsumerModal,
    [isMeterChanged, isUtilityConsumerModal]
  );

  const isChangeMeterOptionVisible = useMemo(
    () =>
      (applicationIdsForChangeMeter.includes(applicationTypeId) &&
        applicationTypeId !== applicationTypes["Removal of Connection"]) ||
      isUtilityConsumerModal,
    [applicationTypeId, isUtilityConsumerModal]
  );

  useEffect(() => {
    (async () => {
      if (consumerId) {
        try {
          await getAvailableMeters({
            applicationId: applicationId ?? "",
            utilityConsumerId: consumerId,
            utility: getOptionValue("utilityOptions", "Power"),
          });
        } catch (error) {
          console.error(error);
        }
      }
    })();
  }, [consumerId]);

  return (
    <Grid container columnSpacing={4}>
      <Grid item xs={12} sm={6}>
        <Text>
          <b>{t("Consumer Name")}: </b>
          {name}
        </Text>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Text>
          <b>{t("Service Number")}: </b>
          {serviceNo}
        </Text>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Text>
          <b>{t("Contract Load")}: </b>
          {contractLoad}
        </Text>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Text>
          <b>{t("Type of Connection")}: </b>
          {typeOfConnection}
        </Text>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Text>
          <b>{t("Address")}: </b>
          {address}
        </Text>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Text>
          <b>{t("Type of Tarrif")}: </b>
          {getOptionLabel("tariff", typeOfTariff)}
        </Text>
      </Grid>
      <Grid item xs={12} sm={6}>
        <div className="d-flex align-items-center">
          {isChangeMeterOptionVisible && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={isMeterChanged}
                  onChange={(_, isChecked) =>
                    setFieldValue("extraValues.isMeterChanged", isChecked)
                  }
                />
              }
              label="Change Meter"
              className="mb-4"
            />
          )}
          {isListOfMetersVisible && (
            <div className="flex-1">
              <SelectField
                formik={formik}
                label={t("Meter Number")}
                name="extraValues.meterId"
                options={[...meters, otherOption]}
                value={
                  meters.find(({ value }) => value === meterId) ?? otherOption
                }
                sx={{ maxWidth: "150px" }}
                loading={isLoading}
              />
            </div>
          )}
        </div>
      </Grid>
    </Grid>
  );
};

ConsumerDetails.propTypes = {
  formik: PropTypes.object,
  applicationTypeId: PropTypes.number,
  isUtilityConsumerModal: PropTypes.bool,
  applicationId: PropTypes.string,
  t: PropTypes.func,
};

export default withTranslation()(ConsumerDetails);
